import { useState, useRef, useEffect } from 'react';
import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'classnames';
import { formatKyivDate } from 'helpers/formatKyivDate';
import { getDayString } from 'helpers/getDayString';
import { formatDate } from 'helpers/formatDate';
import { shortWeekDays } from 'constants/daysOfWeek';
import { monthNames } from 'constants/monthNames';
import { Tooltip } from 'components/Tooltip/Tooltip';
import styles from 'components/DatePicker/DatePicker.scss';

export const DatePicker = ({
  className,
  calendarClassName,
  date,
  handleScrollDate,
  showDay = false,
  enableCalendar = false,
  minDate = null,
  maxDate = null,
  minDateTooltip = 'Вибрано мінімальну дату',
  maxDateTooltip = 'Вибрано максимальну дату',
}) => {
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date(date));
  const [showCalendar, setShowCalendar] = useState(false);
  const [showMinTooltip, setShowMinTooltip] = useState(false);
  const [showMaxTooltip, setShowMaxTooltip] = useState(false);
  const animationTimeoutRef = useRef(null);
  const calendarRef = useRef(null);

  const compareDates = (date1, date2) => {
    if (!date1 || !date2) return 0;
    return date1.localeCompare(date2);
  };

  const isMinDateReached = minDate && compareDates(date, minDate) <= 0;
  const isMaxDateReached = maxDate && compareDates(date, maxDate) >= 0;

  useEffect(() => {
    setCurrentMonth(new Date(date));
  }, [date]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        hideCalendar();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      clearTimeout(animationTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (showCalendar) {
      setIsCalendarVisible(true);
    } else {
      animationTimeoutRef.current = setTimeout(() => {
        setIsCalendarVisible(false);
      }, 300);
    }
  }, [showCalendar]);

  const handleMonthChange = (increment) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + increment);
    setCurrentMonth(newMonth);
  };

  const handleYearChange = (increment) => {
    const newMonth = new Date(currentMonth);
    newMonth.setFullYear(newMonth.getFullYear() + increment);
    setCurrentMonth(newMonth);
  };

  const handleDateSelect = (selectedDate) => {
    const diffTime = selectedDate.getTime() - new Date(date).getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    handleScrollDate(diffDays);
    hideCalendar();
  };

  const toggleCalendar = () => {
    if (enableCalendar) {
      if (showCalendar) {
        hideCalendar();
      } else {
        setShowCalendar(true);
      }
    }
  };

  const hideCalendar = () => {
    setShowCalendar(false);
  };

  const handleScrollDateWithValidation = (direction) => {
    if (direction === -1 && isMinDateReached) return;
    if (direction === 1 && isMaxDateReached) return;
    handleScrollDate(direction);
  };

  const generateCalendarDays = () => {
    const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
    const startDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    const today = new Date();
    const days = [];

    for (let i = 0; i < startDay; i++) {
      days.push(<div key={`empty-${i}`} className={styles.calendarDay}></div>);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const dayDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i);

      const dayDateString = formatKyivDate(dayDate);

      const isDisabled =
        (minDate && compareDates(dayDateString, minDate) < 0) || (maxDate && compareDates(dayDateString, maxDate) > 0);

      const isToday =
        today.getDate() === dayDate.getDate() &&
        today.getMonth() === dayDate.getMonth() &&
        today.getFullYear() === dayDate.getFullYear();

      const isCurrentDate =
        dayDate.getDate() === new Date(date).getDate() &&
        dayDate.getMonth() === new Date(date).getMonth() &&
        dayDate.getFullYear() === new Date(date).getFullYear();

      days.push(
        <div
          key={`day-${i}`}
          className={cn(styles.calendarDay, {
            [styles.currentDay]: isCurrentDate,
            [styles.todayDay]: isToday,
            [styles.disabledDay]: isDisabled,
          })}
          onClick={() => !isDisabled && handleDateSelect(dayDate)}
        >
          {i}
        </div>
      );
    }

    return days;
  };

  return (
    <div className={cn(styles.datePickerContainer, className)}>
      <div className={styles.dateSelect}>
        <div
          className={styles.arrowWrapper}
          onMouseEnter={() => isMinDateReached && setShowMinTooltip(true)}
          onMouseLeave={() => setShowMinTooltip(false)}
        >
          <button
            className={cn(styles.scrollArrow, { [styles.disabled]: isMinDateReached })}
            onClick={() => handleScrollDateWithValidation(-1)}
            type='button'
            disabled={isMinDateReached}
          >
            <FontAwesomeIcon icon={faAngleLeft} />
          </button>
          {minDate && (
            <Tooltip
              text={minDateTooltip}
              className={cn(styles.arrowTooltip, { [styles.active]: showMinTooltip && isMinDateReached })}
            />
          )}
        </div>

        <button
          className={cn(styles.dateDisplay, { [styles.clickable]: enableCalendar, [styles.opened]: isCalendarVisible })}
          onClick={toggleCalendar}
          type='button'
        >
          {showDay ? (
            <div>
              <span className={styles.day}>{getDayString(date)}</span>
              <span className={styles.date}>
                {formatDate(date, 'UA')}
                {enableCalendar && <FontAwesomeIcon className={styles.calendarIcon} icon={faCalendar} />}
              </span>
            </div>
          ) : (
            <>
              {formatDate(date, 'UA')}
              {enableCalendar && <FontAwesomeIcon className={styles.calendarIcon} icon={faCalendar} />}
            </>
          )}
        </button>

        <div
          className={styles.arrowWrapper}
          onMouseEnter={() => isMaxDateReached && setShowMaxTooltip(true)}
          onMouseLeave={() => setShowMaxTooltip(false)}
        >
          <button
            className={cn(styles.scrollArrow, { [styles.disabled]: isMaxDateReached })}
            onClick={() => handleScrollDateWithValidation(1)}
            type='button'
            disabled={isMaxDateReached}
          >
            <FontAwesomeIcon icon={faAngleRight} />
          </button>
          {maxDate && (
            <Tooltip
              text={maxDateTooltip}
              className={cn(styles.arrowTooltip, { [styles.active]: showMaxTooltip && isMaxDateReached })}
            />
          )}
        </div>
      </div>

      {isCalendarVisible && enableCalendar && (
        <div
          className={cn(styles.calendarContainer, calendarClassName, {
            [styles.calendarHidden]: !showCalendar,
          })}
          ref={calendarRef}
        >
          <div className={styles.calendarHeader}>
            <button className={styles.scrollArrow} onClick={() => handleYearChange(-1)} type='button'>
              <FontAwesomeIcon icon={faAngleLeft} />
              <FontAwesomeIcon icon={faAngleLeft} />
            </button>
            <button className={styles.scrollArrow} onClick={() => handleMonthChange(-1)} type='button'>
              <FontAwesomeIcon icon={faAngleLeft} />
            </button>
            <div className={styles.currentMonthYear}>
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </div>
            <button className={styles.scrollArrow} onClick={() => handleMonthChange(1)} type='button'>
              <FontAwesomeIcon icon={faAngleRight} />
            </button>
            <button className={styles.scrollArrow} onClick={() => handleYearChange(1)} type='button'>
              <FontAwesomeIcon icon={faAngleRight} />
              <FontAwesomeIcon icon={faAngleRight} />
            </button>
          </div>
          <div className={styles.calendarWeekDays}>
            {shortWeekDays.map((day) => (
              <div key={day} className={styles.weekDay}>
                {day}
              </div>
            ))}
          </div>
          <div className={styles.calendarDays}>{generateCalendarDays()}</div>
        </div>
      )}
    </div>
  );
};
